import { StyleProp, TextStyle, View } from 'react-native'
import { Text } from 'react-native-paper'
import { createStyle } from '../../contexts/global-styles.provider'

export type StatusType =
  | 'active'
  | 'cancelled'
  | 'pending'
  | 'pending_renovation'
  | 'payment-required'
  | 'paid'
  | 'completed'
  | 'processing'
  | 'failed'
  | 'expired'
  | 'default'

interface StatusBadgeProps {
  status: string
  label?: string
}

export const StatusBadge = ({ status, label }: StatusBadgeProps) => {
  const styles = useStyle() as Record<string, StyleProp<TextStyle>>
  const normalizedStatus = status.toLowerCase() as StatusType

  const statusType = getStatusType(normalizedStatus)
  const statusBadgeStyle = getStatusBadgeStyle(statusType)
  const statusBadgeTextStyle = getStatusBadgeTextStyle(statusType)
  const displayLabel = label || status

  return (
    <View style={styles[statusBadgeStyle]}>
      <Text
        variant="labelSmall"
        style={styles[statusBadgeTextStyle] as StyleProp<TextStyle>}
      >
        {displayLabel}
      </Text>
    </View>
  )
}

const getStatusType = (status: string): StatusType => {
  if (status === 'paid' || status === 'completed') {
    return 'paid'
  }
  if (
    status === 'pending' ||
    status === 'processing' ||
    status === 'pending_renovation'
  ) {
    return 'pending'
  }
  if (status === 'failed' || status === 'cancelled') {
    return 'cancelled'
  }
  if (status === 'expired') {
    return 'expired'
  }
  if (status === 'active') {
    return 'active'
  }
  if (status === 'payment-required') {
    return 'payment-required'
  }
  return 'default'
}

const getStatusBadgeStyle = (type: StatusType): string => {
  const typeMap: Record<StatusType, string> = {
    active: 'statusBadgeActive',
    cancelled: 'statusBadgeCancelled',
    pending: 'statusBadgePending',
    pending_renovation: 'statusBadgePending',
    'payment-required': 'statusBadgePaymentRequired',
    paid: 'statusBadgePaid',
    completed: 'statusBadgePaid',
    processing: 'statusBadgePending',
    failed: 'statusBadgeCancelled',
    expired: 'statusBadgeExpired',
    default: 'statusBadgeDefault',
  }
  return typeMap[type] || 'statusBadgeDefault'
}

const getStatusBadgeTextStyle = (type: StatusType): string => {
  const typeMap: Record<StatusType, string> = {
    active: 'statusBadgeTextActive',
    cancelled: 'statusBadgeTextCancelled',
    pending: 'statusBadgeTextPending',
    pending_renovation: 'statusBadgeTextPending',
    'payment-required': 'statusBadgeTextPaymentRequired',
    paid: 'statusBadgeTextPaid',
    completed: 'statusBadgeTextPaid',
    processing: 'statusBadgeTextPending',
    failed: 'statusBadgeTextCancelled',
    expired: 'statusBadgeTextExpired',
    default: 'statusBadgeTextDefault',
  }
  return typeMap[type] || 'statusBadgeTextExpired'
}

const useStyle = createStyle(({ theme }) => ({
  statusBadgeActive: {
    backgroundColor: theme.colors.primaryContainer,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextActive: {
    color: theme.colors.onPrimaryContainer,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgeCancelled: {
    backgroundColor: theme.colors.errorContainer,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextCancelled: {
    color: theme.colors.onErrorContainer,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgePending: {
    backgroundColor: theme.colors.secondaryContainer,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextPending: {
    color: theme.colors.onSecondaryContainer,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgePaymentRequired: {
    backgroundColor: theme.colors.tertiaryContainer,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextPaymentRequired: {
    color: theme.colors.onTertiaryContainer,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgePaid: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextPaid: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgeExpired: {
    backgroundColor: theme.colors.errorContainer,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextExpired: {
    color: theme.colors.onErrorContainer,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadgeDefault: {
    backgroundColor: theme.colors.surfaceVariant,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(0.5),
    borderRadius: 12,
  },
  statusBadgeTextDefault: {
    color: theme.colors.onSurfaceVariant,
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
}))
